import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { OpenCashDto } from '../../../dtos/open-cash-dto.model';
import { OrderDetailDto } from '../../../dtos/order-detail-dto.model';
import { AccountingService } from '../../../services/accounting.service';
import { OrderService } from '../../../services/order.service';
import { PricingService } from '../../../services/pricing.service';
import { SwalAlert } from '../../../helpers/alert';
import { ReservationDto } from 'src/app/dtos/reservation-dto.model';
import { OrderReservationDto } from 'src/app/dtos/order-reservation-dto.model';

@Component({
  selector: 'app-deposit',
  templateUrl: './deposit.component.html',
  styleUrls: ['./deposit.component.css', '../config-sets.component.css']
})

export class DepositComponent implements OnInit {
  private readonly _accountingService: AccountingService;
  private readonly _pricingService: PricingService;
  @Input() orderDetailDto: OrderDetailDto = new OrderDetailDto();
  @Output() enableSave = new EventEmitter<boolean>();
  loading = false;
  visibleTextValue = '';
  applyDollarErrorText: string ='Reservation amount cannot exceed available credit';
  totalApplyDollarErrortext = 'Reservation amount cannot exceed available credit';
  tableHeader = ['', 'Credit', 'Date', 'Ref #', 'Amount', 'Balance', 'Apply $'];
  openCashDtos: OpenCashDto[] = [];
  remainingDepositAllowed = 0;
  showApplyError: boolean = false;
  showTotalApplyError: boolean = false;
  isEditMode: boolean = false;
  swalAlert = new SwalAlert();

  constructor(
    accountingService: AccountingService,
    public orderService: OrderService,
    pricingService: PricingService) {
      this._accountingService = accountingService;
      this._pricingService = pricingService;
    }

  ngOnInit(): void {
    this.orderService.depositConfigContext = true;
    this.isEditMode = this.orderService.depositConfigIsEditMode;
    this.enableSave.next(false);
    this.getOpenCashCreditMemos();
    if(this.openCashDtos.length === 0 ){
      this.enableSave.next(false);
    }
  }

  async getOpenCashCreditMemos() {
    this.visibleTextValue = "Fetching Deposits";
    this.loading = true;

    const data = await this._accountingService.getOpenCash(this.orderService.orderDto.billToDto.id);
      
    this.openCashDtos = data;
    this.enableSave.next(false);
    this.populateSelectionFromOrderDetails();
    this.populateSelectionFromCache();

    const hasAnySelected = this.openCashDtos.some(item => item.isSelected);
    if (hasAnySelected) {
      this.enableSave.next(true);
    }

    if (this.isEditMode) {
      this.enableSave.next(true);
    }

    this.calculateRemainingDepositAllowed();
    this.loading = false;      

    // this._accountingService.getOpenCash(this.orderService.orderDto.billToDto.id)
    //   .subscribe(data => {
    //     this.openCashDtos = data;
    //     this.enableSave.next(false);
    //     if (this.orderDetailDto.oti !== '' && this.orderDetailDto.oti !== null) {
    //       for (let i = 0; i < this.orderService.orderDto.orderDetailDtos.length; i++) {
    //         if (this.orderService.orderDto.orderDetailDtos[i].oti !== '' && this.orderService.orderDto.orderDetailDtos[i].oti !== null && 
    //         this.orderService.orderDto.orderDetailDtos[i].productCode === this.orderDetailDto.productCode && 
    //         this.orderService.orderDto.orderDetailDtos[i].orderDetailStatus !== 'V') {
    //           const index = this.openCashDtos.findIndex(o => o.originalTransactionId === this.orderService.orderDto.orderDetailDtos[i].oti);

    //           if (index > -1) {
    //             this.openCashDtos[index].isSelected = true;
    //             this.openCashDtos[index].apply = this.orderService.orderDto.orderDetailDtos[i].unitPrice;
    //             this.openCashDtos[index].originalApply = this.openCashDtos[index].apply;
    //             this.enableSave.next(false);
    //           }
    //         }
    //       }
    //     }
    //     if (this.isEditMode) {
    //       this.enableSave.next(true);
    //     }

    //     this.calculateRemainingDepositAllowed();
    //     this.loading = false;
    //   }, () => {
    //     this.swalAlert.alert('Error occurred while retrieving Open cash memos');
    //     this.loading = false;
    //   });
  }

  private populateSelectionFromOrderDetails(): void {
    const currentProductCode = (this.orderDetailDto.productCode ?? '').toUpperCase();

    for (const orderDetail of this.orderService.orderDto.orderDetailDtos) {
      if (!orderDetail.oti ||
        orderDetail.orderDetailStatus === 'V' ||
        (orderDetail.productCode ?? '').toUpperCase() !== currentProductCode) {
        continue;
      }

      const index = this.openCashDtos.findIndex(o => o.originalTransactionId === orderDetail.oti);
      if (index > -1) {
        this.openCashDtos[index].isSelected = true;
        this.openCashDtos[index].apply = orderDetail.listPrice;
        this.openCashDtos[index].originalApply = this.openCashDtos[index].apply;
      }
    }
  }

  private populateSelectionFromCache(): void {
    if (!this.orderService.depositOpenCashDtosCache.length) {
      return;
    }

    for (const cachedOpenCash of this.orderService.depositOpenCashDtosCache.filter(item => item.isSelected)) {
      const index = this.openCashDtos.findIndex(o => o.originalTransactionId === cachedOpenCash.originalTransactionId);
      if (index > -1) {
        this.openCashDtos[index].isSelected = true;
        this.openCashDtos[index].apply = cachedOpenCash.apply;
        this.openCashDtos[index].originalApply = cachedOpenCash.originalApply ?? cachedOpenCash.apply;
      }
    }
  }

  isApplyAmountDisabled(openCash: OpenCashDto): boolean {
    const openCashId = String(openCash.originalTransactionId ?? '').trim();
    if (!openCashId) {
      return false;
    }

    const depositDetailsForOpenCash = this.orderService.orderDto.orderDetailDtos.filter(orderDetail =>
      (orderDetail.productCode ?? '').toUpperCase() === 'DEPOSIT' &&
      String(orderDetail.oti ?? '').trim() === openCashId &&
      orderDetail.orderDetailStatus !== 'V'
    );

    const hasEditableOpenDetail = depositDetailsForOpenCash.some(orderDetail => orderDetail.orderDetailStatus === ' ');
    if (hasEditableOpenDetail) {
      return false;
    }

    return depositDetailsForOpenCash.some(orderDetail => orderDetail.orderDetailStatus === 'C');
  }

  async validateApplyDollar(openCash: OpenCashDto) {
    openCash.apply = -Math.abs(openCash.apply);
   
  
    if (Math.abs(openCash.apply) > Math.abs(openCash.balance)) {
      openCash.apply = 0;
      this.showApplyError = false;
      openCash.isSelected = false;
      await this.swalAlert.alert('Reservation amount cannot exceed available credit');
      return;
    }
    else {
      this.showApplyError = false;
      openCash.isSelected = openCash.apply !== 0;
    }

    this.calculateRemainingDepositAllowed();

    if (this.remainingDepositAllowed < 0) {
      this.showTotalApplyError = true;
    } else {
      this.showTotalApplyError = false;
      const isAnyCheck = this.openCashDtos.some(item => item.isSelected);
      this.enableSave.next(isAnyCheck);
    }

    this.validateEnablingSaveBtn();
  }

  calculateRemainingDepositAllowed(): void {
    let totalApplyDollar = this.openCashDtos.filter(item => item.apply).reduce((sum, item) => sum + Math.abs(item.apply), 0);
    let totalExtendedPrice = this.orderService.orderDto.orderDetailDtos.filter(item => item.orderDetailStatus === ' ' )
    .reduce((sum, item) => sum + item.extendedPrice + item.taxAmount, 0);
    totalExtendedPrice = this._pricingService.round(totalExtendedPrice, 2);
    totalApplyDollar = this._pricingService.round(totalApplyDollar, 2);
    this.remainingDepositAllowed = this._pricingService.round(totalExtendedPrice - totalApplyDollar, 2);
  }

  save(): boolean {
    let totalApplyDollar = this.openCashDtos.filter(item => item.apply).reduce((sum, item) => sum + Math.abs(item.apply), 0);
    let totalExtendedPrice = this.orderService.orderDto.orderDetailDtos.filter(item => item.orderDetailStatus === ' ' )
    .reduce((sum, item) => sum + item.extendedPrice + item.taxAmount, 0);
    totalExtendedPrice = this._pricingService.round(totalExtendedPrice, 2);
    totalApplyDollar = this._pricingService.round(totalApplyDollar, 2);
    if (totalApplyDollar > totalExtendedPrice) {
      this.swalAlert.alert('Reservation amount cannot exceed available credit.');
      return false;
    }

    // If the user had just entered the item into the cart, we will remove it, and add the items selected.
    this.orderService.orderDto.orderDetailDtos = this.orderService.orderDto.orderDetailDtos.filter(item => item !== this.orderDetailDto || item.oti);
    //this.orderService.orderDto.reservations=[];
   for (const openCash of this.openCashDtos.filter(item => item.isSelected)) {
      //Transfer openCash information from the this.openCasDtos to the this.orderService.orderDto.reservations
      const openCashId = String(openCash.originalTransactionId ?? '').trim();
      const openCashInvoiceId = openCash.invoiceId || null;
      const matchingReservations = this.orderService.orderDto.reservations.filter(item =>
        String(item.peopleSoftOpenCashId ?? '').trim() === openCashId &&
        (item.invoiceId || null) === openCashInvoiceId
      );
      let reservation = matchingReservations.find(item => item.statusCode === 'O');
    if(reservation)
    {
      reservation.amountToApply = openCash.apply;
      reservation.invoiceId = openCashInvoiceId;
    }else if (matchingReservations.length === 0){
      let newReservationDto = new OrderReservationDto();
    //newReservationDto.Id = 0;
    newReservationDto.amountToApply = openCash.apply;
    newReservationDto.peopleSoftOpenCashId = openCashId;
    newReservationDto.invoiceId = openCashInvoiceId;
    newReservationDto.statusCode = 'O';
    this.orderService.orderDto.reservations.push(newReservationDto);
    }
    }

    // Remove deselected items from reservations, but keep those tied to completed order details
    for (const openCash of this.openCashDtos.filter(item => !item.isSelected)) {
      const openCashId = String(openCash.originalTransactionId ?? '').trim();
      const hasCompletedOrderDetail = this.orderService.orderDto.orderDetailDtos.some(orderDetail =>
        orderDetail.orderDetailStatus === 'C' &&
        String(orderDetail.oti ?? '').trim() === openCashId
      );

      // Always remove open reservations for deselected cash, while preserving completed history.
      if (!hasCompletedOrderDetail) {
        this.orderService.orderDto.reservations = this.orderService.orderDto.reservations.filter(
          item => !(String(item.peopleSoftOpenCashId ?? '').trim() === openCashId && item.statusCode === 'O')
        );
      }
    }

    this.saveDepositsConfiguration();
    return true;
  }

  enableSavebtn(): void
  {
    if (this.isEditMode) {
      this.enableSave.next(true);
      this.orderService.depositOpenCashDtosCache = this.openCashDtos.map(item => ({ ...item }));
    } else {
      this.validateEnablingSaveBtn();
    }
  }

    saveDepositsConfiguration() {
    for (const openCashDto of [...this.openCashDtos].reverse()) {
      if (openCashDto.apply < 0) {
        openCashDto.apply = -openCashDto.apply;
      }
 
      const matchingDeposits = this.orderService.orderDto.orderDetailDtos
        .filter(o => o.oti === openCashDto.originalTransactionId && o.productCode === 'DEPOSIT' && o.orderDetailStatus !== 'V');
      const editableDeposit = matchingDeposits.find(o => o.orderDetailStatus === ' ');
      const hasCompletedDeposit = matchingDeposits.some(o => o.orderDetailStatus === 'C');
 
      // Delete or void unselected items
      if (!openCashDto.isSelected && editableDeposit) {
        if (editableDeposit.id <= 0) {
          // Delete the row if it hasn't been saved to the database.
          this.orderService.orderDto.orderDetailDtos = this.orderService.orderDto.orderDetailDtos.filter(item => item !== editableDeposit);
        } else if (editableDeposit.orderDetailStatus === ' ') {
          // Void the row if it has been saved to the database.
          editableDeposit.orderDetailStatus = 'V'
          editableDeposit.voidDate = new Date();
          editableDeposit.voidReasonCode = '14';
          editableDeposit.voidedQuantity = editableDeposit.openQuantity;
          editableDeposit.openQuantity = 0;
          editableDeposit.orderQuantity = 0;
          editableDeposit.taxAmount = 0;
          editableDeposit.taxRate = 0;
        }
      // Update the selected record if it already exists on the order.
      } else if (openCashDto.isSelected && editableDeposit) {
        editableDeposit.listPrice = -openCashDto.apply;
        editableDeposit.discount = 100;
        editableDeposit.unitPrice = 0;
        editableDeposit.extendedPrice = 0;
        editableDeposit.oti = openCashDto.originalTransactionId;
        editableDeposit.referenceNumber = openCashDto.referenceNumber.trim();
        editableDeposit.creditMemoNumber = openCashDto.invoiceNumber ? openCashDto.invoiceNumber : null;
      }
      // Add the selected record if it doesn't exist on the order.
      else if (openCashDto.isSelected && !editableDeposit && !hasCompletedDeposit){
        const newDeposit = this.orderService.createNewOrderDetail(this.orderService.orderDto, 'DEPOSIT');
        newDeposit.openQuantity = 1;
        newDeposit.orderQuantity = 1;
        newDeposit.listPrice = -openCashDto.apply;
        newDeposit.unitPrice = 0;
        newDeposit.extendedPrice = 0;
        newDeposit.discount =100;
        newDeposit.oti = openCashDto.originalTransactionId;
        newDeposit.referenceNumber = openCashDto.referenceNumber.trim();
        newDeposit.creditMemoNumber = openCashDto.invoiceNumber ? openCashDto.invoiceNumber : null;
        this.orderService.orderDto.orderDetailDtos.unshift(newDeposit);
        this.orderService.orderDto.orderDetailDtos = [...this.orderService.orderDto.orderDetailDtos];
      }
    }
 
    this.setPaymentTermsOnSave();
  }
 
  setPaymentTermsOnSave(){
    if (this.orderDetailDto.productCode === 'DEPOSIT') {
      this.orderService.orderDto.paymentMethod = 'C';
      this.orderService.orderDto.ccToken = null;
      this.orderService.orderDto.ccLastFour = null;
      this.orderService.orderDto.ccExpirationDate = null;
      this.orderService.orderDto.ccAuthorizationCode = null;
      if(this.orderService.orderDto.paymentTerms==="Credit Card")
      {
        this.orderService.orderDto.paymentTerms = "Net 15 Days";
      }
    }
  }

  validateEnablingSaveBtn(): void {
    const openCashDtos: OpenCashDto[] = this.openCashDtos.filter(e => e.isSelected);
    let selectedCount = 0;
    let valueEnteredCount = 0;
    openCashDtos.forEach(e => {
      if (e['isSelected']) {
        selectedCount ++;
      }
      if (e['isSelected'] && e['apply']) {
        valueEnteredCount++;
      }
    })
    selectedCount === valueEnteredCount ? this.enableSave.next(true) : this.enableSave.next(false);
    this.orderService.depositOpenCashDtosCache = selectedCount === valueEnteredCount
      ? openCashDtos.map(item => ({ ...item }))
      : [];
  }
}
